import DOMPurify from 'dompurify'

/**
 * The student answer allowlist, client side: what the answer editor's
 * toolbar can produce (inline formatting, lists, alignment, direction) and
 * nothing else — no links, images, iframes, styles. Used to seed the editor
 * (innerHTML executes markup immediately) and to render stored answers.
 *
 * Stored answers are ALWAYS HTML: the backend normalises every text answer
 * on write (rich-editor HTML is sanitized, textarea text is escaped) and
 * legacy rows were converted once. Nothing here sniffs for markup — a value
 * such as "a<b then c>d" arrives already escaped.
 * Twin of the backends' ContentSanitizer student allowlist.
 */
export function sanitizeStudentHtml(html: string | null | undefined): string {
	if (!html) return ''
	// No USE_PROFILES: a profile widens the allowlist back to the full HTML
	// set and silently drops the ALLOWED_* options below. No ALLOWED_URI_REGEXP
	// either: nothing in this allowlist carries a URI, and DOMPurify matches
	// every non-URI-safe attribute value (dir="rtl") against it.
	return DOMPurify.sanitize(html, {
		ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup', 'ol', 'ul', 'li', 'span', 'blockquote', 'pre', 'code'],
		ALLOWED_ATTR: ['class', 'dir', 'data-list'],
		FORBID_ATTR: ['style'],
	})
}

/**
 * HTML → plain text for the textarea fallback's initial value: one line per
 * block, <br> as a line break, entities decoded. Inverse of the backend's
 * escapePlainToHtml for the markup the student toolbar produces; richer
 * formatting flattens to text (the fallback only exists when the editor
 * chunk failed to load).
 */
export function htmlToPlainText(html: string | null | undefined): string {
	if (!html) return ''
	if (typeof DOMParser === 'undefined') return html
	const body = new DOMParser().parseFromString(html, 'text/html').body
	const lines: string[] = []
	const walk = (node: Node): void => {
		node.childNodes.forEach(child => {
			if (child.nodeType === Node.TEXT_NODE) {
				lines[lines.length - 1] = (lines[lines.length - 1] ?? '') + (child.textContent ?? '')
				return
			}
			if (child.nodeType !== Node.ELEMENT_NODE) return
			const tag = (child as Element).tagName
			if (tag === 'BR') {
				lines.push('')
				return
			}
			const isBlock = ['P', 'DIV', 'LI', 'BLOCKQUOTE', 'PRE', 'OL', 'UL'].includes(tag)
			if (isBlock && lines.length && lines[lines.length - 1] !== '') lines.push('')
			if (isBlock && !lines.length) lines.push('')
			walk(child)
			if (isBlock && ['P', 'DIV', 'LI', 'BLOCKQUOTE', 'PRE'].includes(tag)) lines.push('')
		})
	}
	lines.push('')
	walk(body)
	// A block that only held a <br> produced two breaks; collapse the trailing one.
	return lines
		.join('\n')
		.replace(/\n{3,}/g, '\n\n')
		.replace(/^\n+|\n+$/g, '')
}
